import React, { memo } from "react";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";

import { Attachment } from "./attachment";
import { Boolean } from "./boolean";
import { Text } from "./text";
import { TextArea } from "./textarea";
import { Number } from "./number";
import { MultiSelect } from "./multiselect";
import { SingleSelect } from "./singleselect";
import { SingleUser } from "./singleuser";
import { MultiUser } from "./multiuser";
import { DateComponent } from "./date";
import { DateTime } from "./datetime";
import { Signature } from "./signature";
import { Email } from "./email";
import { Slider } from "./slider";
import { Rating } from "./rating";
import { Lookup } from "./lookup";
import { Image } from "./image";
import { Checklist } from "./checklist";
import { RemoteLookup } from "./remotelookup";
import { Currency } from "./currency";

import styles from "./style.css";

export function CellRenderer(props) {
  const { column, value, row, isLoading, instanceId } = props;
  if (isLoading) {
    return (
      <span className={styles.loading}>
        <div className="skeleton-loader">
          <Trans>Loading</Trans>
        </div>
      </span>
    );
  }

  if (column.renderer) {
    return <column.renderer column={column} value={value} row={row} />;
  }
  switch (
    (column.widgetType && column.widgetType.toLowerCase()) ||
    column.type.toLowerCase()
  ) {
    case "attachment":
      return <Attachment value={value} column={column} row={row} />;
    case "boolean":
      return <Boolean value={value} column={column} row={row} />;
    case "currency":
      return <Currency value={value} column={column} row={row} />;
    case "text":
      return <Text value={value} column={column} row={row} />;
    case "textarea":
      return <TextArea value={value} column={column} row={row} />;
    case "number":
      return <Number value={value} column={column} row={row} />;
    case "multiselect":
    case "checkbox":
      return <MultiSelect value={value} column={column} row={row} />;
    case "user":
      return <SingleUser value={value} column={column} row={row} />;
    case "userandgroup":
      return <SingleUser value={value} column={column} row={row} />;
    case "multiuser":
      return <MultiUser value={value} column={column} row={row} />;
    case "date":
      return <DateComponent value={value} column={column} row={row} />;
    case "datetime":
      return <DateTime value={value} column={column} row={row} />;
    case "select":
      return <SingleSelect value={value} column={column} row={row} />;
    case "signature":
      return (
        <Signature
          value={value}
          column={column}
          row={row}
          instanceId={instanceId}
        />
      );
    case "email":
      return <Email value={value} column={column} row={row} />;
    case "slider":
      return <Slider value={value} column={column} row={row} />;
    case "starrating":
      return <Rating value={value} column={column} row={row} />;
    case "reference":
      return <Lookup value={value} column={column} row={row} />;
    case "image":
      return <Image value={value} column={column} row={row} />;
    case "checklist":
      return <Checklist value={value} column={column} row={row} />;
    case "remotelookup-json-auto":
    case "remotelookup-text-auto":
    case "remotelookup-xml-auto":
    case "remotelookup-json-manual":
    case "remotelookup-text-manual":
    case "remotelookup-xml-manual":
      return <RemoteLookup value={value} column={column} row={row} />;
    default:
      return <Text value={JSON.stringify(value)} column={column} row={row} />;
  }
}

CellRenderer.propTypes = {
  value: PropTypes.any,
  row: PropTypes.any,
  column: PropTypes.any,
  isLoading: PropTypes.bool,
  instanceId: PropTypes.string
};

export function CellComponent(props) {
  const {
    column,
    value,
    row,
    style,
    rowIndex,
    isFixed,
    isLoading,
    isVisible,
    errors,
    appearance,
    isSelected,
    instanceId
  } = props;
  return (
    <div
      className={styles.cell}
      style={style}
      data-column-type={column.type.toLowerCase()}
      data-row={rowIndex}
      data-col={column.id}
      data-fixed={isFixed}
      data-cell={true}
      data-selected={isSelected}
      data-valid={!errors || errors.length === 0}
      data-readonly={column.readOnly}
      data-appearance={appearance}
      data-visible={isVisible}
      data-row-id={row._id}
    >
      {isVisible && (
        <CellRenderer
          value={value}
          column={column}
          row={row}
          isLoading={isLoading}
          instanceId={instanceId}
        />
      )}
    </div>
  );
}

CellComponent.propTypes = {
  value: PropTypes.any,
  row: PropTypes.any,
  column: PropTypes.any,
  style: PropTypes.any,
  colIndex: PropTypes.number,
  rowIndex: PropTypes.number,
  isFixed: PropTypes.bool,
  errors: PropTypes.array,
  appearance: PropTypes.string,
  isSelected: PropTypes.bool,
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool,
  instanceId: PropTypes.string
};

export const Cell = memo(CellComponent);
